import { Enemy } from "../Enemy";
import { Game } from "../Game";
import { IBehaviour } from "./IBehaviour";
import { Geom } from "../Geom";
import { Bullet, createPooledBullet } from "../Bullet";
import { Timer } from "../Timer";
import { Color, ColorSource } from "pixi.js";

enum State {
	Moving,
	Charging,
	Firing,
}

export interface IBulletPattern {
	readonly hasFinished: boolean;

	start(ranged: StationaryRanged, enemy: Enemy, game: Game): void;

	tick(deltaMS: number, ranged: StationaryRanged, enemy: Enemy, game: Game): void;
}

export class RadialBulletPattern implements IBulletPattern {
	duration: number = 1000;
	count: number = 10;

	public get hasFinished() {
		return this.duration <= 0;
	}

	start(ranged: StationaryRanged, enemy: Enemy, game: Game): void {
		this.duration = 1000;
		for (let angle = 0; angle < 2 * Math.PI; angle += 2 * Math.PI / this.count) {
			let bullet = createPooledBullet(game.camera.container,
				enemy.x,
				enemy.y,
				angle,
				ranged.bulletSize,
				false,
				enemy.damage
			);
			bullet.speed = ranged.bulletSpeed;
			game.bullets.push(bullet);
		}
	}

	tick(deltaMS: number, ranged: StationaryRanged, enemy: Enemy, game: Game): void {
		this.duration -= deltaMS;
	}
}

export class TrackingBulletPattern implements IBulletPattern {
	maxCount: number = 4;
	count: number = this.maxCount;
	maxSleepDuration: number = 300;
	sleepDuration: number = this.maxSleepDuration;

	public get hasFinished() {
		return this.count <= 0;
	}

	start(ranged: StationaryRanged, enemy: Enemy, game: Game): void {
		this.count = this.maxCount;
		this.sleepDuration = 0;
	}

	tick(deltaMS: number, ranged: StationaryRanged, enemy: Enemy, game: Game): void {
		this.sleepDuration -= deltaMS;
		if (this.sleepDuration <= 0) {
			let bullet = createPooledBullet(game.camera.container,
				enemy.x,
				enemy.y,
				Geom.angle(enemy, game.player),
				ranged.bulletSize,
				false,
				enemy.damage,
			);
			bullet.speed = ranged.bulletSpeed;
			game.bullets.push(bullet);
			this.sleepDuration = this.maxSleepDuration;
			this.count--;
		}
	}
}

export class StationaryRanged implements IBehaviour {
	public state: State = State.Moving;
	public chargeDuration: number = 0;
	public maxChargeDuration: number = 2000;
	public lastUpdatedDirection: Timer = new Timer();
	public angle: number = 0;
	public bulletSize: number = 0;
	public bulletSpeed: number = 0;
	public minDistance = 100;
	public maxDistance = 300;
	public fireOnSight: boolean = true;
	public pattern: IBulletPattern = new RadialBulletPattern();

	public tick(deltaMS: number, enemy: Enemy, game: Game): void {
		this.chargeDuration -= deltaMS;
		this.lastUpdatedDirection.tick(deltaMS);

		const distanceToPlayer = Geom.distance(game.player, enemy);

		if (this.lastUpdatedDirection.elapsed > 1000) {
			this.angle = Geom.angle(game.player, enemy);
			this.lastUpdatedDirection.reset();
		}

		if (this.state == State.Charging) {
			if (distanceToPlayer < this.minDistance) {
				this.state = State.Moving;
				this.chargeDuration = 0;
			} else if (distanceToPlayer > this.maxDistance) {
				this.state = State.Moving;
			} else if (this.chargeDuration <= 0) {
				this.state = State.Firing;
				this.pattern.start(this, enemy, game);
			}
		} else if (this.state == State.Firing) {
			this.pattern.tick(deltaMS, this, enemy, game);
			if (this.pattern.hasFinished) {
				this.state = State.Charging;
				this.chargeDuration = this.maxChargeDuration;
			}
		} else if (this.state == State.Moving) {
			if (distanceToPlayer < this.minDistance) {
				Geom.moveInDirection(enemy, this.angle, enemy.speed * deltaMS);
			} else if (distanceToPlayer > this.maxDistance) {
				Geom.moveInDirection(enemy, this.angle, -enemy.speed * deltaMS);
			} else if (this.state == State.Moving) {
				this.state = State.Charging;
			}
		}
	}
}
